import { StepStatus, Stepper, StepperStep } from "@dgs/core";
import { FormikErrors, useFormikContext } from "formik";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import styled from "styled-components";
import { EventDetailResource, EventOffboardingFormValues } from "../../types/event";
import { shouldSkipCostsStep, shouldSkipKeyMetricsStep } from "./eventOffboardingStepperUtils";

export const isKeyMetricsStepValid = (errors: FormikErrors<EventOffboardingFormValues>) => {
	return (
		!errors.customersMetric &&
		!errors.employeesMetric &&
		!errors.registrationsMetric &&
		!errors.visitorsMetric &&
		!errors.eventGoalRatingMetric &&
		!errors.eventReachMetric &&
		!errors.invitedPersonsMetric
	);
};

export const isCostsStepValid = (errors: FormikErrors<EventOffboardingFormValues>) => {
	return !errors.costCategories;
};

export type EventOffboardingStepKey = "key-metrics" | "costs";

export interface EventOffboardingStep extends StepperStep {
	key: EventOffboardingStepKey;
	isValid: (errors: FormikErrors<EventOffboardingFormValues>) => boolean;
}

const StyledStepper = styled(Stepper)`
	flex-wrap: wrap;
	row-gap: ${({ theme }) => theme.spacing(4)};

	${({ theme }) => theme.breakpoints.desktop} {
		grid-column: span 2;
	}
`;

const getStatus = (index: number, currentIndex: number, isValid: boolean): StepStatus => {
	if (index === currentIndex) {
		return "active";
	}

	if (index < currentIndex) {
		if (isValid) {
			return "done";
		} else {
			return "error";
		}
	}

	return "inactive";
};

export const offboardingSteps: EventOffboardingStep[] = [
	{ label: "Key Metrics", isValid: isKeyMetricsStepValid, key: "key-metrics" },
	{ label: "Costs", isValid: isCostsStepValid, key: "costs" },
];

interface Props {
	event: EventDetailResource;
}

export const EventOffboardingStepper: FC<Props> = ({ event }) => {
	const { pathname } = useLocation();
	const { t } = useTranslation();
	const formik = useFormikContext<EventOffboardingFormValues>();

	const { filteredSteps } = useMemo(() => {
		let filteredSteps = offboardingSteps;
		if (shouldSkipKeyMetricsStep(event.eventType)) {
			filteredSteps = filteredSteps.filter((step) => step.key !== "key-metrics");
		}
		if (shouldSkipCostsStep(event.eventType)) {
			filteredSteps = filteredSteps.filter((step) => step.key !== "costs");
		}
		return { filteredSteps };
	}, [event.eventType]);

	const indexOfActiveStep = filteredSteps.findIndex((step) => pathname.includes(step.key));

	if (filteredSteps.length != 2 || indexOfActiveStep === -1) {
		return null;
	}

	return (
		<StyledStepper
			currentStepIndex={indexOfActiveStep}
			steps={filteredSteps.map((s, index) => ({
				...s,
				label: t(s.label as string),
				status: getStatus(index, indexOfActiveStep, s.isValid(formik.errors)),
			}))}
		></StyledStepper>
	);
};
